import { environment } from 'src/environments/environment';

export interface ChannelSetupApiEnvironment {
  protocol: string;
  host: string;
  port: string;
  apiBaseUrl?: string;
  channelSetupApiPrefix: string;
}

const DEFAULT_CHANNEL_SETUP_API: ChannelSetupApiEnvironment = {
  protocol: 'http',
  host: 'localhost',
  port: '5002',
  channelSetupApiPrefix: '/api/v1/channel-setup',
};

function getBillingLikeEnv(): Partial<ChannelSetupApiEnvironment> {
  const billing = (environment as { billing?: Partial<ChannelSetupApiEnvironment> }).billing ?? {};
  const next: Partial<ChannelSetupApiEnvironment> = {};
  if (billing.protocol) {
    next.protocol = billing.protocol;
  }
  if (billing.host) {
    next.host = billing.host;
  }
  if (billing.port) {
    next.port = billing.port;
  }
  if (billing.apiBaseUrl) {
    next.apiBaseUrl = billing.apiBaseUrl;
  }
  if (billing.channelSetupApiPrefix) {
    next.channelSetupApiPrefix = billing.channelSetupApiPrefix;
  }
  return next;
}

function getChannelSetupEnv(): ChannelSetupApiEnvironment {
  return { ...DEFAULT_CHANNEL_SETUP_API, ...getBillingLikeEnv() };
}

function getServiceOrigin(): string {
  const env = getChannelSetupEnv();
  const host = env.host || (environment as { host?: string }).host || 'localhost';
  return `${env.protocol}://${host}:${env.port}`;
}

function getChannelSetupBaseUrl(): string {
  const env = getChannelSetupEnv();
  const prefix = env.channelSetupApiPrefix.startsWith('/')
    ? env.channelSetupApiPrefix
    : `/${env.channelSetupApiPrefix}`;
  const baseUrl = env.apiBaseUrl?.trim();
  if (baseUrl) {
    return `${baseUrl.replace(/\/+$/, '')}${prefix}`;
  }
  return `${getServiceOrigin()}${prefix}`;
}

export function getChannelSetupConfigUrl(): string {
  return `${getChannelSetupBaseUrl()}/config`;
}

export function getChannelSetupProvidersUrl(): string {
  return `${getChannelSetupBaseUrl()}/providers`;
}

export function getChannelSetupTemplatesUrl(): string {
  return `${getChannelSetupBaseUrl()}/templates`;
}

export function getChannelSetupFilterTimingUrl(): string {
  return `${getChannelSetupBaseUrl()}/filter-timing`;
}

export function getChannelSetupBlacklistUrl(): string {
  return `${getChannelSetupBaseUrl()}/blacklist`;
}

export function getChannelSetupBlacklistEntriesUrl(): string {
  return `${getChannelSetupBaseUrl()}/blacklist/entries`;
}

export function getChannelSetupBlacklistImportUrl(): string {
  return `${getChannelSetupBaseUrl()}/blacklist/entries/import`;
}

export function getChannelSetupUnsubscribeEntriesUrl(): string {
  return `${getChannelSetupBaseUrl()}/unsubscribe/entries`;
}

export function getChannelSetupUnsubscribeEntriesCountUrl(): string {
  return `${getChannelSetupBaseUrl()}/unsubscribe/entries/count`;
}

export function getChannelSetupOnDemandUrl(): string {
  return `${getChannelSetupBaseUrl()}/on-demand`;
}

function getNotificationsV1BaseUrl(): string {
  const env = getChannelSetupEnv();
  const baseUrl = env.apiBaseUrl?.trim();
  const prefix = '/api/notifications/v1';
  if (baseUrl) {
    return `${baseUrl.replace(/\/+$/, '')}${prefix}`;
  }
  return `${getServiceOrigin()}${prefix}`;
}

export function getEmailTemplateUploadUrl(): string {
  return `${getNotificationsV1BaseUrl()}/template_upload`;
}

export function getInviteEmailTemplateUploadUrl(): string {
  return `${getChannelSetupBaseUrl()}/invite-email-template`;
}

export function getInviteEmailTemplateByIdUrl(): string {
  return `${getChannelSetupBaseUrl()}/invite-email-template`;
}

export function getEmailTemplateByIdUrl(): string {
  return `${getNotificationsV1BaseUrl()}/template_get_by_id`;
}

export function getEmailTemplateUpdateUrl(): string {
  return `${getNotificationsV1BaseUrl()}/template_update_by_id`;
}
